/**
 * Página reutilizable para Distribución (/logistica/distribucion) e Historial (/logistica/historial).
 * Props:
 *   titulo       string   — título de la página
 *   estadoFijo   string   — si se pasa, fuerza ese estado y oculta el selector
 */
import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { salidasApi } from '../api/salidas'
import { retirosApi } from '../api/remitos'
import { fmtFecha, fmtFechaHora, toISOLocal } from '../utils/fecha'
import { copiarTexto } from '../utils/copiar'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import Boton from '../components/Boton'
import Paginador from '../components/Paginador'
import { colors, font, text, radius, sx } from '../design/tokens'

// El mapa (Leaflet) se carga solo si se abre — no engorda el bundle principal.
const MapaReparto = lazy(() => import('../components/MapaReparto'))

const ESTADO_CFG = {
  armando:    { bg: '#fef3c7', color: '#92400e', label: 'Armando' },
  despachada: { bg: '#dbeafe', color: '#1e40af', label: 'En reparto' },
  cerrada:    { bg: '#dcfce7', color: '#166534', label: 'Cerrada' },
}

/**
 * Resultado derivado de una salida cerrada, según el detalle de sus documentos.
 * No cambia el estado real: solo comunica si la salida terminó bien, con fallos o mixta.
 */
function resultadoCerrada(s) {
  const total = s.documentos_count || 0
  const ent   = s.entregados_count || 0
  const no    = s.no_entregados_count || 0
  const rea   = s.reagendados_count || 0
  if (total > 0 && ent === total)        return { bg: '#dcfce7', color: '#166534', label: '✓ Completada' }
  if (ent === 0 && (no + rea) > 0)       return { bg: '#fee2e2', color: '#991b1b', label: '✕ No entregada' }
  if (no > 0 || rea > 0)                 return { bg: '#ffedd5', color: '#9a3412', label: '◑ Parcial' }
  return { bg: '#dcfce7', color: '#166534', label: 'Cerrada' }
}

// Clasifica una salida en una categoría filtrable de la UI (mismo criterio que los badges)
function categoriaSalida(s) {
  if (s.estado !== 'cerrada') return 'en_reparto'
  const total = s.documentos_count || 0
  const ent = s.entregados_count || 0
  const no = s.no_entregados_count || 0
  const rea = s.reagendados_count || 0
  if (total > 0 && ent === total) return 'completada'
  if (ent === 0 && (no + rea) > 0) return 'no_entregada'
  if (no > 0 || rea > 0) return 'parcial'
  return 'completada'
}

const RESULTADOS = [
  { value: 'completada',   label: '✓ Completada' },
  { value: 'parcial',      label: '◑ Parcial' },
  { value: 'no_entregada', label: '✕ No entregada' },
  { value: 'en_reparto',   label: '🚚 En reparto' },
]

function EstadoBadge({ salida }) {
  const cfg = salida.estado === 'cerrada'
    ? resultadoCerrada(salida)
    : (ESTADO_CFG[salida.estado] || { bg: '#f1f5f9', color: '#475569', label: salida.estado })
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 10, fontSize: '0.75rem', fontWeight: 600, background: cfg.bg, color: cfg.color }}>
      {cfg.label}
    </span>
  )
}

// Devuelve { desde, hasta } en formato YYYY-MM-DD para cada preset
function rangoPreset(preset) {
  const hoy = new Date()
  const fmt = (d) => toISOLocal(d)
  const hace = (dias) => { const d = new Date(hoy); d.setDate(d.getDate() - dias); return d }
  switch (preset) {
    case 'hoy':   return { desde: fmt(hoy),    hasta: fmt(hoy) }
    case 'ayer':  return { desde: fmt(hace(1)), hasta: fmt(hace(1)) }
    case '7dias': return { desde: fmt(hace(6)), hasta: fmt(hoy) }
    case '30dias':return { desde: fmt(hace(29)),hasta: fmt(hoy) }
    default:      return { desde: '',           hasta: '' }
  }
}

const PRESETS = [
  { value: 'hoy',    label: 'Hoy' },
  { value: 'ayer',   label: 'Ayer' },
  { value: '7dias',  label: 'Últimos 7 días' },
  { value: '30dias', label: 'Últimos 30 días' },
  { value: 'custom', label: 'Personalizar' },
]

export default function SalidasAgrupadasPage({ titulo, estadoFijo }) {
  const navigate = useNavigate()
  const [salidas, setSalidas]   = useState([])
  const [meta, setMeta]         = useState(null)
  const [loading, setLoading]   = useState(true)
  const [estado, setEstado]     = useState(estadoFijo || '')
  const [resultado, setResultado] = useState('') // filtro derivado (solo historial)
  const [busqueda, setBusqueda]   = useState('') // busca por #envio (id) o nombre de fletero
  const [pagina, setPagina]     = useState(1)
  const [qrSalida, setQrSalida] = useState(null)   // salida cuyo QR se muestra
  const [copiado, setCopiado]   = useState(null)    // id de salida con link recién copiado
  const [retiros, setRetiros]   = useState(null)    // resumen de la cola de retiros (solo distribución) — la cola vive en /logistica/retiros
  const [verMapa, setVerMapa]   = useState(false)   // 🗺 mapa de reparto (solo distribución)
  const esDistribucion = estadoFijo === 'despachada'

  // Link de la hoja de ruta del fletero
  const linkGuia = (s) => `${window.location.origin}/fletero/salida/${s.token_acceso}`

  const compartirWhatsApp = (s) => {
    const tel = (s.fletero?.telefono || '').replace(/\D/g, '')
    const destino = tel ? (tel.startsWith('54') ? tel : '54' + tel) : ''
    const msg = `Hola ${s.fletero?.nombre || ''}, esta es tu hoja de ruta de hoy:\n${linkGuia(s)}`
    const url = destino
      ? `https://wa.me/${destino}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`
    window.open(url, '_blank')
  }

  const copiarLink = async (s) => {
    if (await copiarTexto(linkGuia(s))) { setCopiado(s.id); setTimeout(() => setCopiado(null), 1800) }
  }

  // Filtros de fecha — solo para historial (sin estadoFijo)
  const [preset, setPreset]         = useState(!estadoFijo ? '30dias' : '')
  const [customDesde, setCustomDesde] = useState('')
  const [customHasta, setCustomHasta] = useState('')
  // Para distribución: filtro de fecha simple
  const [fecha, setFecha]           = useState('')

  const cargar = useCallback(async (p = 1, { silent = false } = {}) => {
    if (!silent) setLoading(true)
    try {
      const params = { page: p }
      if (estado) params.estado = estado

      if (!estadoFijo) {
        // Historial: presets o personalizado
        const rango = preset === 'custom'
          ? { desde: customDesde, hasta: customHasta }
          : rangoPreset(preset)
        if (rango.desde) params.fecha_desde = rango.desde
        if (rango.hasta) params.fecha_hasta  = rango.hasta
      } else {
        // Distribución: fecha simple opcional
        if (fecha) params.fecha = fecha
      }

      const res = await salidasApi.listar(params)
      setSalidas(res.data.data)
      setMeta(res.data)

      // En distribución, un resumen de los retiros por sucursal (no son viajes:
      // la cola completa vive en /logistica/retiros, Martin 28 ago 2026).
      if (esDistribucion) {
        try {
          const r = await retirosApi.listar()
          const demora = r.data.demora_dias ?? 3
          const pend = r.data.pendientes || []
          const dias = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
          setRetiros({
            total: pend.length,
            sinAviso: pend.filter(x => !x.disponible_retiro_at).length,
            demorados: pend.filter(x => x.disponible_retiro_at && dias(x.disponible_retiro_at) >= demora).length,
            porSucursal: (r.data.sucursales || []).map(su => ({ id: su.id, nombre: su.nombre, n: pend.filter(x => x.sucursal?.id === su.id).length })).filter(su => su.n > 0),
          })
        } catch { setRetiros(null) }
      }
    } catch {
      if (!silent) setSalidas([])
    } finally {
      if (!silent) setLoading(false)
    }
  }, [fecha, estado, preset, customDesde, customHasta, estadoFijo, esDistribucion])


  useEffect(() => { cargar(pagina) }, [cargar, pagina])
  useAutoRefresh(() => cargar(pagina, { silent: true }), 30000)

  // Distribución: solo salidas con docs verdaderamente pendientes (null estado_entrega)
  // Los no_entregado/reagendado sin pendientes → la salida se cierra automáticamente al liberar → va a historial
  const salidasVisibles = (estadoFijo === 'despachada'
    ? salidas.filter(s => (s.pendientes_count || 0) > 0)
    : salidas)
    // Filtro de resultado (derivado, client-side) — solo en historial
    .filter(s => !resultado || categoriaSalida(s) === resultado)
    // Buscador #envio: matchea por nº de salida (id) o nombre de fletero
    .filter(s => {
      const q = busqueda.trim().toLowerCase().replace(/^#/, '')
      if (!q) return true
      return String(s.id).includes(q)
        || (s.fletero?.nombre || '').toLowerCase().includes(q)
    })

  const gruposPorFletero = useMemo(() => {
    const m = new Map()
    salidasVisibles.forEach(s => {
      const k = s.fletero?.id ?? '_sin'
      if (!m.has(k)) m.set(k, { fletero: s.fletero || null, salidas: [], totalDocs: 0, totalEnt: 0, totalPen: 0, totalNo: 0 })
      const g = m.get(k)
      g.salidas.push(s)
      g.totalDocs += s.documentos_count || 0
      g.totalEnt  += s.entregados_count || 0
      g.totalPen  += s.pendientes_count || 0
      g.totalNo   += s.no_entregados_count || 0
    })
    return [...m.values()].sort((a, b) => {
      // Los canales internos (retiro en sucursal/depósito) van al FINAL:
      // primero el reparto en la calle, que es lo que se gestiona acá.
      const ai = a.fletero?.interno ? 1 : 0
      const bi = b.fletero?.interno ? 1 : 0
      if (ai !== bi) return ai - bi

      return (a.fletero?.nombre || '').localeCompare(b.fletero?.nombre || '')
    })
  }, [salidasVisibles])

  const pct = (ent, total) => total === 0 ? 0 : Math.round((ent / total) * 100)

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div style={st.headerRow}>
        <h1 style={st.h1}>{titulo}</h1>
        {esDistribucion && (
          <Boton variante="secondary" sm onClick={() => setVerMapa(v => !v)}>
            {verMapa ? '✕ Cerrar mapa' : '🗺 Mapa de reparto'}
          </Boton>
        )}
      </div>

      {esDistribucion && verMapa && (
        <Suspense fallback={<p style={{ color: colors.textMeta }}>Cargando mapa…</p>}>
          <MapaReparto />
        </Suspense>
      )}

      <div style={st.filtros}>
        <input
          type="search"
          placeholder="Buscar #envio o fletero..."
          value={busqueda}
          onChange={e => { setBusqueda(e.target.value); setPagina(1) }}
          style={{ ...st.input, minWidth: 220 }}
        />
        {!estadoFijo ? (
          /* Historial: selector de presets */
          <>
            <div style={st.presetGroup}>
              {PRESETS.map(p => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => { setPreset(p.value); setPagina(1) }}
                  style={{ ...st.presetBtn, ...(preset === p.value ? st.presetBtnActive : {}) }}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {preset === 'custom' && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="date" value={customDesde} onChange={e => { setCustomDesde(e.target.value); setPagina(1) }} style={st.input} />
                <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>→</span>
                <input type="date" value={customHasta} onChange={e => { setCustomHasta(e.target.value); setPagina(1) }} style={st.input} />
              </div>
            )}
            <select value={resultado} onChange={e => { setResultado(e.target.value); setPagina(1) }} style={st.input}>
              <option value="">Todos los resultados</option>
              {RESULTADOS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </>
        ) : (
          /* Distribución: fecha simple */
          <>
            <input type="date" value={fecha} onChange={e => { setFecha(e.target.value); setPagina(1) }} style={st.input} />
            <Boton variante="ghost" onClick={() => { setFecha(''); setPagina(1) }}>Limpiar</Boton>
          </>
        )}
      </div>

      {loading ? (
        <p style={{ color: '#94a3b8' }}>Cargando...</p>
      ) : salidas.length === 0 && (!esDistribucion || !retiros?.total) ? (
        <div style={st.vacio}>
          <p style={{ fontSize: '2rem', marginBottom: 8 }}>📦</p>
          <p>No hay salidas registradas.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {gruposPorFletero.map(g => (
            <div key={g.fletero?.id ?? '_sin'} style={st.grupoCard}>
              <div style={st.grupoHeader}>
                <div style={st.grupoTitulo}>
                  <div style={st.avatar}>{(g.fletero?.nombre || 'S').slice(0, 1).toUpperCase()}</div>
                  <div>
                    <h2 style={st.grupoNombre}>{g.fletero?.nombre || 'Sin fletero'}</h2>
                    <p style={st.grupoMeta}>
                      {g.fletero?.vehiculo && <span>🚚 {g.fletero.vehiculo}</span>}
                      {g.fletero?.telefono && <span style={{ marginLeft: '0.75rem' }}>📞 {g.fletero.telefono}</span>}
                    </p>
                  </div>
                </div>
                <div style={st.grupoStats}>
                  <span style={st.statPill}>{g.salidas.length} salida{g.salidas.length !== 1 ? 's' : ''}</span>
                  <span style={{ ...st.statPill, ...st.pillGreen }}>{g.totalEnt} entregados</span>
                  <span style={{ ...st.statPill, ...st.pillYellow }}>{g.totalPen} pendientes</span>
                  {g.totalNo > 0 && <span style={{ ...st.statPill, ...st.pillRed }}>{g.totalNo} no entregados</span>}
                </div>
              </div>

              <table style={st.tabla}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={st.th}>Fecha</th>
                    <th style={st.th}>Estado</th>
                    <th style={st.th}>Docs</th>
                    <th style={st.th}>Progreso</th>
                    <th style={st.th}>Notas</th>
                    <th style={st.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {g.salidas.map(s => {
                    const total = s.documentos_count || 0
                    const ent   = s.entregados_count || 0
                    const pen   = s.pendientes_count || 0
                    const no    = s.no_entregados_count || 0
                    const rea   = s.reagendados_count || 0
                    const p = pct(ent, total)
                    return (
                      <tr key={s.id} style={st.tr}>
                        <td style={st.td}>
                          <div style={{ fontWeight: 600 }}>{fmtFecha(s.fecha)}</div>
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>#{s.id}</div>
                        </td>
                        <td style={st.td}><EstadoBadge salida={s} /></td>
                        <td style={st.td}>
                          <strong style={{ fontSize: '0.95rem' }}>{total}</strong>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                            {ent}✓ · {pen}⏱ · {no}✕{rea > 0 ? ` · ${rea}↻` : ''}
                          </div>
                        </td>
                        <td style={st.td}>
                          <div style={st.progressWrap}>
                            <div style={{ ...st.progressFill, width: `${p}%`, background: p === 100 ? '#16a34a' : p > 0 ? '#3b82f6' : '#e2e8f0' }} />
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#475569', marginTop: 2 }}>{p}%</div>
                        </td>
                        <td style={{ ...st.td, color: '#64748b', fontSize: '0.8125rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.notas || <span style={{ color: '#cbd5e1' }}>—</span>}
                        </td>
                        <td style={{ ...st.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {esDistribucion && s.token_acceso && !s.fletero?.interno && (
                            <>
                              <Boton variante="send" sm style={{ marginRight: 4 }} onClick={() => compartirWhatsApp(s)} title={`Enviar guía por WhatsApp a ${s.fletero?.nombre || 'el fletero'}`}>📱 Enviar guía</Boton>
                              <button onClick={() => copiarLink(s)} style={st.btnIcono} title="Copiar link de la guía">{copiado === s.id ? '✓' : '🔗'}</button>
                              <button onClick={() => setQrSalida(s)} style={st.btnIcono} title="Ver QR de la guía">▦</button>
                            </>
                          )}
                          {esDistribucion && s.fletero?.interno && (
                            <span style={{ fontSize: '0.72rem', color: colors.textSec, fontWeight: 700, marginRight: 6 }}>🏬 Retira</span>
                          )}
                          <Boton variante="secondary" sm onClick={() => navigate(`/salidas/${s.id}`)}>Ver →</Boton>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ))}

          {/* Retiros — no son viajes. La cola completa (por sucursal, con
              avisos, demoras y lista para separar) vive en /logistica/retiros. */}
          {esDistribucion && retiros && retiros.total > 0 && (
            <Link to="/logistica/retiros" style={st.retirosResumen} title="Abrir Retiros por sucursal">
              <span style={st.retirosTitulo}>🏬 Retiros por sucursal</span>
              <span style={st.retirosBadge}>{retiros.total}</span>
              <span style={st.retirosDetalle}>
                {retiros.porSucursal.map(su => `${su.nombre} ${su.n}`).join(' · ')}
                {retiros.sinAviso > 0 && <> · <b>{retiros.sinAviso} sin avisar</b></>}
                {retiros.demorados > 0 && <> · <b style={{ color: colors.warn }}>{retiros.demorados} demorados</b></>}
              </span>
              <span style={st.retirosVer}>Ver la cola →</span>
            </Link>
          )}
        </div>
      )}

      {meta && meta.last_page > 1 && (
        <Paginador pagina={pagina} totalPaginas={meta.last_page} onCambiar={setPagina} />
      )}

      {/* Modal QR de la hoja de ruta */}
      {qrSalida && (
        <div style={st.qrOverlay}>
          <div style={st.qrModal} onClick={e => e.stopPropagation()}>
            <h3 style={st.qrTitulo}>Hoja de ruta — {qrSalida.fletero?.nombre || 'fletero'}</h3>
            <p style={st.qrSub}>Salida #{qrSalida.id} · escaneá con la cámara del celular</p>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(linkGuia(qrSalida))}`}
              alt="QR de la hoja de ruta" style={st.qrImg}
            />
            <code style={st.qrLink}>{linkGuia(qrSalida)}</code>
            <div style={st.qrBtns}>
              <Boton variante="send" sm onClick={() => compartirWhatsApp(qrSalida)}>📱 WhatsApp</Boton>
              <Boton variante="secondary" sm onClick={() => copiarLink(qrSalida)}>{copiado === qrSalida.id ? '✓ copiado' : '🔗 copiar'}</Boton>
              <Boton variante="ghost" sm onClick={() => setQrSalida(null)}>Cerrar</Boton>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const st = {
  headerRow:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  h1:          { fontSize: '1.5rem', fontWeight: 700, color: '#1e293b', margin: 0 },
  filtros:        { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center', background: '#fff', padding: '0.75rem', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,.06)' },
  presetGroup:    { display: 'flex', gap: 4, flexWrap: 'wrap' },
  presetBtn:      { padding: '6px 13px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer', color: '#475569' },
  presetBtnActive:{ background: '#1e40af', color: '#fff', border: '1px solid #1e40af' },
  input:          { padding: '7px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.875rem' },
  vacio:       { textAlign: 'center', padding: '60px 0', color: '#94a3b8' },
  // Resumen de retiros: una línea que lleva a /logistica/retiros (la cola completa).
  retirosResumen:{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.85rem 1.25rem', background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,.07)', textDecoration: 'none', color: colors.text, flexWrap: 'wrap' },
  retirosTitulo: { fontWeight: 700, color: colors.text, fontSize: 15 },
  retirosBadge:  { ...sx.mono, background: colors.bgSoft, color: colors.textSub, borderRadius: radius.pill, padding: '2px 10px', fontSize: text.sec, fontWeight: 700, border: `1px solid ${colors.border}` },
  retirosDetalle:{ color: colors.textSec, fontSize: text.sec },
  retirosVer:    { marginLeft: 'auto', color: colors.link, fontWeight: 600, fontSize: text.sec, whiteSpace: 'nowrap' },
  grupoCard:   { background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,.07)', overflow: 'hidden' },
  grupoHeader: { padding: '1rem 1.25rem', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', background: 'linear-gradient(180deg,#f8fafc 0%,white 100%)' },
  grupoTitulo: { display: 'flex', alignItems: 'center', gap: '0.875rem' },
  avatar:      { width: 44, height: 44, borderRadius: '50%', background: '#1e40af', color: 'white', fontWeight: 800, fontSize: '1.125rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  grupoNombre: { margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1e293b' },
  grupoMeta:   { margin: '2px 0 0', fontSize: '0.8125rem', color: '#64748b' },
  grupoStats:  { display: 'flex', gap: '0.4rem', flexWrap: 'wrap' },
  statPill:    { fontSize: '0.7rem', fontWeight: 700, padding: '0.2rem 0.625rem', borderRadius: 10, background: '#f1f5f9', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.03em' },
  pillGreen:   { background: '#dcfce7', color: '#166534' },
  pillYellow:  { background: '#fef3c7', color: '#92400e' },
  pillRed:     { background: '#fee2e2', color: '#991b1b' },
  tabla:       { width: '100%', borderCollapse: 'collapse' },
  th:          { padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' },
  tr:          { borderTop: '1px solid #f1f5f9' },
  td:          { padding: '10px 14px', fontSize: '0.875rem', color: '#1e293b', verticalAlign: 'middle' },
  progressWrap:{ width: 110, height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' },
  progressFill:{ height: '100%', transition: 'width 0.2s' },
  btnIcono:    { background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, padding: '5px 9px', fontWeight: 700, cursor: 'pointer', fontSize: '0.8rem', marginRight: 4 },
  qrOverlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  qrModal:     { background: '#fff', borderRadius: 14, padding: 24, width: 320, textAlign: 'center', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' },
  qrTitulo:    { margin: '0 0 4px', fontSize: '1rem', fontWeight: 700, color: '#1e293b' },
  qrSub:       { margin: '0 0 14px', fontSize: '0.78rem', color: '#64748b' },
  qrImg:       { width: 240, height: 240, borderRadius: 8, border: '1px solid #e2e8f0' },
  qrLink:      { display: 'block', fontSize: '0.68rem', color: '#94a3b8', wordBreak: 'break-all', margin: '10px 0 14px' },
  qrBtns:      { display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' },
}
